import React, { useMemo } from 'react'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import PromotionInclusionList from 'components/Cruises/PromotionInclusions/PromotionInclusionList'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import TextLink from 'components/Luxkit/TextLink'
import Tooltip from 'components/Luxkit/Tooltip'

interface Props {
  isFlashOffer: boolean;
  shipDetailsLabel?: string;
  itineraryDetailsLabel: string;
  offerDescription?: string;
  promotionDetails: App.CruisePromotionDetails | null;
  evergreenInclusions?: Array<App.EvergreenInclusion>;
  tileInclusions?: Array<App.TileInclusion>;
  cities?: string[];
  earliestDepartureDate: string;
  laterDates: Array<string>;
}

function CruiseTileDetails({
  isFlashOffer,
  shipDetailsLabel,
  itineraryDetailsLabel,
  offerDescription,
  promotionDetails,
  evergreenInclusions,
  tileInclusions,
  cities,
  earliestDepartureDate,
  laterDates,
}: Props) {
  const hasInclusions = useMemo(() => (
    promotionDetails?.sellingPoints.length || evergreenInclusions?.length || tileInclusions?.length
  ), [promotionDetails, evergreenInclusions, tileInclusions])

  const showDescription = useMemo(() => {
    const poists = hasInclusions || 0
    if (poists > 1) return false
    return true
  }, [hasInclusions])

  return <Group gap={12} direction="vertical">
    <Group gap={4} direction="vertical">
      {shipDetailsLabel && <BodyText variant="medium" colour="neutral-two">{shipDetailsLabel}</BodyText>}

      <Group gap={8} direction="horizontal" verticalAlign="center" wrap="wrap">
        <BodyText variant="medium" colour="neutral-two">
          {itineraryDetailsLabel}
        </BodyText>

        {cities && (
          <Tooltip description={cities.join(' - ')}>
            <TextLink weight="regular" size="caption">
              <CSSBreakpoint min="desktop">View itinerary</CSSBreakpoint>
              <CSSBreakpoint max="tablet"><SolidInfoCircleIcon size="XS" /></CSSBreakpoint>
            </TextLink>
          </Tooltip>
        )}
      </Group>
    </Group>

    <Group gap={4} direction="vertical">
      {offerDescription && showDescription &&
        <MarkdownRender
          type="compact"
          fontSize="medium"
          fontWeight="normal"
          content={offerDescription}
        />
      }
      {!!hasInclusions && <PromotionInclusionList
        promotionDetails={promotionDetails}
        evergreenInclusions={evergreenInclusions}
        tileInclusions={tileInclusions}
      />}
    </Group>

    <BodyText variant="medium" colour="neutral-two" weight="bold">
      {earliestDepartureDate && `Departing on ${earliestDepartureDate}`}{' '}
      {!laterDates.length && isFlashOffer && 'and more'}
      {!!laterDates.length &&
        <Tooltip description={laterDates.join(', ')}>
          <TextLink>
            and more
          </TextLink>
        </Tooltip>}
    </BodyText>
  </Group>
}

export default CruiseTileDetails
